import { useQueryClient } from "@tanstack/react-query"
import {
  defaultRangeExtractor,
  useVirtualizer,
  type Range,
} from "@tanstack/react-virtual"
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FocusEvent,
  type ReactNode,
} from "react"
import { getApiErrorCode } from "../../../../../api/apiError"
import {
  statementArticlesKey,
  useStatementArticles,
} from "../../../../../api/declarationsApi"

/** Rows the table keeps in the DOM beyond the visible window, each side. */
const OVERSCAN = 12
/** A sub-row's height before it is measured (one field line). */
const ROW_ESTIMATE = 44
/** Rows per request. The endpoint's own default; at most 1,000. */
const PAGE_SIZE = 500
/** Restarts from page 1 after a rejected cursor before the editor's list takes over. */
const MAX_RESTARTS = 1

/** What a rendered row must carry so the virtualizer can measure it and a
 *  screen reader can place it in the whole list. Spread onto the `<tr>`. */
export interface ArticleRowProps {
  ref: (node: Element | null) => void
  "data-index": number
  "aria-rowindex": number
}

export interface PagedArticleRowsProps {
  declId: string
  statementId: string
  /** The substance's persisted CAS (`""` for the nameless slot). */
  substance: string
  /** The table's own scroll box: the window the rows are virtualized in. */
  scrollElement: HTMLDivElement | null
  /** Columns of the table, so a placeholder row spans the whole width. */
  columnCount: number
  /** The article list the editor already holds: rendered as before (still
   *  virtualized) when the paged read is unavailable (an older backend), cannot
   *  finish, or disagrees with it on any loaded row. */
  fallbackArticles: readonly string[]
  renderRow: (
    articleNumber: string,
    name: string | null,
    rowProps: ArticleRowProps,
  ) => ReactNode
  loadingLabel: string
}

/**
 * STEP C4: the per-article rows of one substance, one page at a time and only the
 * rows in view. A 10,000-article substance used to put every row in the DOM at once
 * and freeze the tab (sandbox DEC-2026-0007); here the article list comes from
 * `GET /{id}/statements/{sid}/articles` (today's list and order, exactly) and the
 * virtualizer draws the ~30 rows on screen, fetching the next page as the reviewer
 * scrolls towards it. The cells themselves are the caller's (`renderRow`): their
 * values are read from the same condition rows every save writes into, so an edit
 * shows at once and never waits for a page to refetch.
 *
 * Rendered as `<tbody>` spacer rows around the visible slice, so the table keeps
 * its columns, header and widths exactly as the unpaged one draws them.
 *
 * Dragging the scrollbar straight to the end fetches every page in turn (20 for
 * 10k): the DOM stays bounded, so the tab never freezes; accepted for now.
 */
export function PagedArticleRows({
  declId,
  statementId,
  substance,
  scrollElement,
  columnCount,
  fallbackArticles,
  renderRow,
  loadingLabel,
}: PagedArticleRowsProps) {
  const params = { substance, limit: PAGE_SIZE }
  const pages = useStatementArticles(declId, statementId, params)
  const loaded = useMemo(
    () => pages.data?.pages.flatMap((p) => p.items) ?? [],
    [pages.data],
  )
  const loadedPages = pages.data?.pages.length ?? 0
  // A cursor the server no longer accepts (400 CURSOR_MISMATCH: the request it was
  // made for changed under it) is not a failure: THIS list (its exact key, no other
  // substance's) starts again from its first page, once. Rejected on the FIRST page
  // (no cursor sent), or again after a restart, it falls back to the editor's own
  // list like anything else. While it restarts the editor's list stays on screen, so
  // a row kept mounted for its draft is never swapped for a loading line.
  const queryClient = useQueryClient()
  const [restarting, setRestarting] = useState(false)
  // The cap is per list and per trouble: another list (statement or substance) starts
  // with a fresh one, and so does this list once a restart got it past page 1 again.
  const listKey = `${declId}|${statementId}|${substance ?? ""}`
  const [restarts, setRestarts] = useState({ key: listKey, count: 0 })
  if (restarts.key !== listKey) setRestarts({ key: listKey, count: 0 })
  const rejected =
    pages.isError &&
    loadedPages > 0 &&
    getApiErrorCode(pages.error) === "CURSOR_MISMATCH"
  const cursorRejected =
    rejected && restarts.key === listKey && restarts.count < MAX_RESTARTS
  const exactKey = statementArticlesKey(declId, statementId, params)
  useEffect(() => {
    if (!cursorRejected) return
    setRestarts((r) => ({ ...r, count: r.count + 1 }))
    setRestarting(true)
    void queryClient
      .resetQueries({ queryKey: exactKey, exact: true })
      .finally(() => setRestarting(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the key is its parts
  }, [cursorRejected, queryClient, declId, statementId, substance])
  // The server's list must BE the editor's, row for row: the cells under each row
  // write to its article, so a row that names a different article than the editor's
  // list at that position (a binding changed at the same count, a duplicate across
  // pages, a list the editor narrowed on its own) would route an edit to the wrong
  // article. Any disagreement draws the editor's own list instead; checking the
  // loaded rows against it costs one pass per page.
  const firstPage = pages.data?.pages[0]
  const disagrees = useMemo(() => {
    if (firstPage === undefined) return false
    if (firstPage.totalUnfiltered !== fallbackArticles.length) return true
    if (loaded.length > fallbackArticles.length) return true
    return loaded.some((item, i) => item.articleNumber !== fallbackArticles[i])
  }, [firstPage, loaded, fallbackArticles])
  // Paging that stopped short (no next page, fewer rows than announced) would leave
  // rows on "Loading" for good: fall back instead.
  const endedShort =
    firstPage !== undefined &&
    !pages.hasNextPage &&
    !pages.isFetching &&
    loaded.length !== firstPage.total
  useEffect(() => {
    if (!pages.isError && loadedPages >= 2 && restarts.count > 0)
      setRestarts((r) => ({ ...r, count: 0 }))
  }, [pages.isError, loadedPages, restarts.count])
  const failed = (pages.isError && !cursorRejected) || disagrees || endedShort
  // Restarting: the editor's own rows hold the place until page 1 is back.
  const showFallback = failed || cursorRejected || restarting
  const total = showFallback ? fallbackArticles.length : (firstPage?.total ?? 0)

  // The row holding focus stays mounted however far the window scrolls: its
  // input keeps an unsaved draft in local state until blur, and a row that
  // unmounts mid-edit would drop it silently.
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null)
  const rangeExtractor = useCallback(
    (range: Range) => {
      const indexes = defaultRangeExtractor(range)
      // A focused index past the end (the list shrank under it) names no row.
      if (
        focusedIndex === null ||
        focusedIndex >= range.count ||
        indexes.includes(focusedIndex)
      )
        return indexes
      return [...indexes, focusedIndex].sort((a, b) => a - b)
    },
    [focusedIndex],
  )
  // ...and is forgotten once the list is shorter than it.
  useEffect(() => {
    if (focusedIndex !== null && focusedIndex >= total) setFocusedIndex(null)
  }, [focusedIndex, total])
  const rowIndexOf = (target: EventTarget | null): number | null => {
    const row =
      target instanceof Element ? target.closest("tr[data-index]") : null
    const raw = row?.getAttribute("data-index")
    return raw == null ? null : Number(raw)
  }
  const onFocus = (e: FocusEvent<HTMLTableSectionElement>) =>
    setFocusedIndex(rowIndexOf(e.target))
  const onBlur = (e: FocusEvent<HTMLTableSectionElement>) => {
    // Focus moving within the same row (Tab between its fields) keeps it.
    if (rowIndexOf(e.relatedTarget) === rowIndexOf(e.target)) return
    setFocusedIndex(null)
  }

  const virtualizer = useVirtualizer({
    count: total,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_ESTIMATE,
    overscan: OVERSCAN,
    rangeExtractor,
    // Rows differ in height (several fields, a Memory annotation, a wrapped name),
    // so each is measured; a row that measures 0 (not laid out: hidden, or a test
    // environment) keeps the estimate rather than collapsing the whole window.
    measureElement: (element) => {
      const height = element.getBoundingClientRect().height
      return height > 0 ? height : ROW_ESTIMATE
    },
    // The box has a fixed max height; this is its first-paint guess, before the
    // browser measures it (and all a test environment ever has).
    initialRect: { width: 800, height: 420 },
  })
  const items = virtualizer.getVirtualItems()
  const lastIndex = items.length ? items[items.length - 1].index : -1

  // Fetch the next page once the window reaches the last loaded row, never while
  // any fetch runs: a scroll fetch started during a refetch (after a statement
  // change) would cancel it and page on from a stale cursor.
  const { hasNextPage, isFetching, fetchNextPage } = pages
  useEffect(() => {
    if (showFallback || !hasNextPage || isFetching) return
    if (lastIndex >= loaded.length - 1)
      void fetchNextPage({ cancelRefetch: false })
  }, [
    showFallback,
    hasNextPage,
    isFetching,
    fetchNextPage,
    lastIndex,
    loaded.length,
  ])

  if (!showFallback && pages.isPending) {
    return (
      <tbody aria-busy>
        <tr aria-rowindex={2}>
          <td colSpan={columnCount} className="sv2-stab-loading">
            {loadingLabel}
          </td>
        </tr>
      </tbody>
    )
  }

  const spacer = (key: string, height: number) =>
    height > 0 ? (
      <tr key={key} aria-hidden style={{ height }}>
        <td colSpan={columnCount} />
      </tr>
    ) : null
  let end = 0
  return (
    <tbody
      data-testid="substance-table-paged"
      aria-busy={pages.isFetching || restarting || undefined}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {items.map((v) => {
        // A gap before this row: the window's start, or the jump to the focused
        // row kept mounted outside it.
        const gap = spacer(`gap-${v.index}`, v.start - end)
        end = v.end
        const rowProps: ArticleRowProps = {
          ref: virtualizer.measureElement,
          "data-index": v.index,
          // Row 1 is the header row.
          "aria-rowindex": v.index + 2,
        }
        let row: ReactNode
        if (showFallback) {
          const n = fallbackArticles[v.index]
          row = n === undefined ? null : renderRow(n, null, rowProps)
        } else {
          const item = loaded[v.index]
          // The focused row past the loaded pages (after a restart brought only page 1
          // back) is drawn from the editor's own list, the same article at the same
          // place, so its input and unsaved draft stay mounted until its page lands.
          const held =
            !item && v.index === focusedIndex
              ? fallbackArticles[v.index]
              : undefined
          row = item ? (
            renderRow(item.articleNumber, item.name, rowProps)
          ) : held !== undefined ? (
            renderRow(held, null, rowProps)
          ) : (
            <tr
              key={`pending-${v.index}`}
              className="sv2-stab-sub"
              {...rowProps}
            >
              <td colSpan={columnCount} className="sv2-stab-loading">
                {loadingLabel}
              </td>
            </tr>
          )
        }
        return (
          <Fragment key={v.index}>
            {gap}
            {row}
          </Fragment>
        )
      })}
      {spacer("after", virtualizer.getTotalSize() - end)}
    </tbody>
  )
}
